import { Fragment } from 'react';
import { Button, Form, Table } from 'semantic-ui-react';
import { displayRepeaterSystemId } from '../../../Constants/gen1Constants';
import { useAppContext } from '../../../Contexts/AppContext';
import { isPurchasedOnlyFlag } from './gen1MasterItemHelpers';

/**
 * Repeater 明細の1行を描画するコンポーネント
 */
function RepeaterDetailRow({
    row,
    systemType,
    systemId,
    otherSystemId,
    validationErrors,
    modelOptions,
    onRepeaterLineChange,
    onDeleteManualRepeaterRow,
    onDeleteSearchRepeaterRow,
    onDeleteRemoveRepeaterRow,
    newPurchaseOnly = false,
}) {
    const isFromOrderSearch =
        row.OperationType === 'ADD' &&
        row.ExchangePairId == null &&
        String(row.OrderNumber ?? '').trim() !== '';
    const isDeletableAdd =
        row.OperationType === 'ADD' &&
        !isFromOrderSearch &&
        row.ExchangePairId == null;
    const isDeletableSearch =
        row.OperationType === 'ADD' &&
        isFromOrderSearch &&
        row.ExchangePairId == null;
    const isDeletableRemove =
        row.OperationType === 'REMOVE' && row.ExchangePairId == null;
    return (
        <Table.Row key={row.LineSequenceNumber}>
            <Table.Cell>{row.OperationType}</Table.Cell>
            <Table.Cell>
                {displayRepeaterSystemId(
                    row,
                    systemType,
                    systemId,
                    otherSystemId
                )}
            </Table.Cell>
            <Table.Cell>
                {isFromOrderSearch ? (
                    <span>{row.BaseModel || '-'}</span>
                ) : (
                    <>
                        <Form.Dropdown
                            selection
                            placeholder="Select Model"
                            options={modelOptions}
                            value={row.BaseModel}
                            onChange={(evn, data) =>
                                onRepeaterLineChange(
                                    row.LineSequenceNumber,
                                    'BaseModel',
                                    data.value
                                )
                            }
                            error={
                                !!validationErrors[
                                    `repeater_${row.LineSequenceNumber ?? 'unknown'}_model`
                                ]
                            }
                        />
                    </>
                )}
            </Table.Cell>
            {!newPurchaseOnly && (
                <Table.Cell>
                    {isFromOrderSearch ? (
                        <span>{row.SerialNumber || '-'}</span>
                    ) : (
                        <>
                            <Form.Input
                                placeholder="Enter Serial Number"
                                value={row.SerialNumber}
                                onChange={(evn, data) =>
                                    onRepeaterLineChange(
                                        row.LineSequenceNumber,
                                        'SerialNumber',
                                        data.value
                                    )
                                }
                                error={
                                    !!validationErrors[
                                        `repeater_${row.LineSequenceNumber ?? 'unknown'}_serialNumber`
                                    ]
                                }
                            />
                        </>
                    )}
                </Table.Cell>
            )}
            <Table.Cell collapsing>
                {isDeletableAdd && onDeleteManualRepeaterRow && (
                    <Button
                        size="small"
                        icon="trash"
                        aria-label="Delete row"
                        onClick={() =>
                            onDeleteManualRepeaterRow(row.LineSequenceNumber)
                        }
                    />
                )}
                {isDeletableSearch && onDeleteSearchRepeaterRow && (
                    <Button
                        size="small"
                        icon="trash"
                        aria-label="Delete row"
                        onClick={() =>
                            onDeleteSearchRepeaterRow(row.LineSequenceNumber)
                        }
                    />
                )}
                {isDeletableRemove && onDeleteRemoveRepeaterRow && (
                    <Button
                        size="small"
                        icon="trash"
                        aria-label="Delete row"
                        onClick={() =>
                            onDeleteRemoveRepeaterRow(row.LineSequenceNumber)
                        }
                    />
                )}
            </Table.Cell>
        </Table.Row>
    );
}

/**
 * Repeater Details のテーブル（Add / Exchange / Remove の行を表示）
 */
export default function RepeaterDetailsTable({
    repeaterLines = [],
    systemType = '',
    systemId = '',
    otherSystemId = '',
    addRepeater,
    repeaterTrunkingQty,
    exchangeRepeater,
    exchangeRepeaterQuantity,
    removeRepeater,
    removeRepeaterQuantity,
    validationErrors = {},
    onRepeaterLineChange,
    onDeleteManualRepeaterRow,
    onDeleteSearchRepeaterRow,
    onDeleteRemoveRepeaterRow,
    onDeleteExchangeRepeaterPair,
    newPurchaseOnly = false,
}) {
    const { Reducer } = useAppContext();
    const isPurchasedRepeaterApplication = !newPurchaseOnly;
    const modelOptions = (Reducer.modelMasterForGen1 || [])
        .filter(
            (x) =>
                isPurchasedRepeaterApplication ||
                !isPurchasedOnlyFlag(x?.IsPurchasedOnly)
        )
        .map((x) => ({
            key: x.Id,
            value: x.ModelName,
            text: x.ModelName,
        }));

    const addCount = addRepeater
        ? Math.max(0, Number(repeaterTrunkingQty) || 0)
        : 0;
    const exchangePairCount = exchangeRepeater
        ? Math.max(0, parseInt(exchangeRepeaterQuantity, 10) || 0)
        : 0;
    const removeCount = removeRepeater
        ? Math.max(0, parseInt(removeRepeaterQuantity, 10) || 0)
        : 0;

    const addLines = repeaterLines.slice(0, addCount);
    const exchangeLines = repeaterLines.slice(
        addCount,
        addCount + exchangePairCount * 2
    );
    const removeLines = repeaterLines.slice(
        addCount + exchangePairCount * 2,
        addCount + exchangePairCount * 2 + removeCount
    );
    const exchangePairs = Array.from({ length: exchangePairCount }, (_, i) => ({
        pairId: i,
        removeLine: exchangeLines[2 * i],
        addLine: exchangeLines[2 * i + 1],
    }))
        .filter((p) => p.removeLine && p.addLine)
        .map((p) => ({
            ...p,
            pairId:
                p.removeLine.ExchangePairId != null
                    ? p.removeLine.ExchangePairId
                    : p.pairId,
        }));

    return (
        <Table celled striped>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>Function</Table.HeaderCell>
                    <Table.HeaderCell>System ID</Table.HeaderCell>
                    <Table.HeaderCell>Model</Table.HeaderCell>
                    {!newPurchaseOnly && (
                        <Table.HeaderCell>Serial Number</Table.HeaderCell>
                    )}
                    <Table.HeaderCell></Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {addLines.map((row) => (
                    <RepeaterDetailRow
                        key={row.LineSequenceNumber}
                        row={row}
                        systemType={systemType}
                        systemId={systemId}
                        otherSystemId={otherSystemId}
                        validationErrors={validationErrors}
                        modelOptions={modelOptions}
                        onRepeaterLineChange={onRepeaterLineChange}
                        onDeleteManualRepeaterRow={onDeleteManualRepeaterRow}
                        onDeleteSearchRepeaterRow={onDeleteSearchRepeaterRow}
                        onDeleteRemoveRepeaterRow={onDeleteRemoveRepeaterRow}
                        newPurchaseOnly={newPurchaseOnly}
                    />
                ))}
                {!newPurchaseOnly &&
                    exchangePairs.map(({ pairId, removeLine, addLine }) => {
                        const pairNumber = pairId + 1;
                        return (
                            <Fragment key={pairId}>
                                <Table.Row>
                                    <Table.Cell
                                        colSpan={4}
                                        style={{
                                            backgroundColor: '#f0f0f0',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Exchange Pair {pairNumber}
                                    </Table.Cell>
                                    <Table.Cell
                                        collapsing
                                        style={{
                                            backgroundColor: '#f0f0f0',
                                        }}
                                    >
                                        {onDeleteExchangeRepeaterPair && (
                                            <Button
                                                size="small"
                                                icon="trash"
                                                aria-label="Delete exchange pair"
                                                onClick={() =>
                                                    onDeleteExchangeRepeaterPair(
                                                        pairId
                                                    )
                                                }
                                            />
                                        )}
                                    </Table.Cell>
                                </Table.Row>
                                <RepeaterDetailRow
                                    key={removeLine.LineSequenceNumber}
                                    row={removeLine}
                                    systemType={systemType}
                                    systemId={systemId}
                                    otherSystemId={otherSystemId}
                                    validationErrors={validationErrors}
                                    modelOptions={modelOptions}
                                    onRepeaterLineChange={onRepeaterLineChange}
                                    onDeleteManualRepeaterRow={
                                        onDeleteManualRepeaterRow
                                    }
                                    onDeleteSearchRepeaterRow={
                                        onDeleteSearchRepeaterRow
                                    }
                                    onDeleteRemoveRepeaterRow={
                                        onDeleteRemoveRepeaterRow
                                    }
                                />
                                <RepeaterDetailRow
                                    key={addLine.LineSequenceNumber}
                                    row={addLine}
                                    systemType={systemType}
                                    systemId={systemId}
                                    otherSystemId={otherSystemId}
                                    validationErrors={validationErrors}
                                    modelOptions={modelOptions}
                                    onRepeaterLineChange={onRepeaterLineChange}
                                    onDeleteManualRepeaterRow={
                                        onDeleteManualRepeaterRow
                                    }
                                    onDeleteSearchRepeaterRow={
                                        onDeleteSearchRepeaterRow
                                    }
                                    onDeleteRemoveRepeaterRow={
                                        onDeleteRemoveRepeaterRow
                                    }
                                />
                            </Fragment>
                        );
                    })}
                {!newPurchaseOnly &&
                    removeLines.map((row) => (
                        <RepeaterDetailRow
                            key={row.LineSequenceNumber}
                            row={row}
                            systemType={systemType}
                            systemId={systemId}
                            otherSystemId={otherSystemId}
                            validationErrors={validationErrors}
                            modelOptions={modelOptions}
                            onRepeaterLineChange={onRepeaterLineChange}
                            onDeleteManualRepeaterRow={
                                onDeleteManualRepeaterRow
                            }
                            onDeleteSearchRepeaterRow={
                                onDeleteSearchRepeaterRow
                            }
                            onDeleteRemoveRepeaterRow={
                                onDeleteRemoveRepeaterRow
                            }
                        />
                    ))}
            </Table.Body>
        </Table>
    );
}
